import type { AiReportSummary } from '@erp/shared';
import { Plus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { formatDate } from '../../lib/intl.js';

import { AiReportCreateDialog } from './AiReportCreateDialog.js';
import { StatusBadge, TYPE_ICON } from './reportMeta.js';
import { useAiReports } from './useAiReports.js';

/**
 * Index of every generated report. Creation opens the dialog; a row opens the
 * viewer. The list re-polls itself only while a report is generating.
 */
export function AiReportsPage(): JSX.Element {
  const { t, i18n } = useTranslation('aiReports');
  const navigate = useNavigate();
  const { data, isLoading } = useAiReports();
  const [createOpen, setCreateOpen] = useState(false);
  // Creating needs ai_reports.manage; a read-only role gets the list without the button.
  const canManage = usePermission('ai_reports.manage');

  const fmtDate = (iso: string): string =>
    formatDate(iso, { locale: i18n.language }, { year: 'numeric', month: 'short', day: 'numeric' });

  const columns: DataTableColumn<AiReportSummary>[] = [
    {
      id: 'title',
      header: t('aiReports.columns.title', { defaultValue: 'Title' }),
      cell: (r) => {
        const Icon = TYPE_ICON[r.type];
        return (
          <span className="flex items-center gap-2">
            <span className="grid h-7 w-7 flex-none place-items-center rounded-md bg-brand/10 text-brand">
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block truncate font-medium" style={{ unicodeBidi: 'plaintext' }}>{r.title}</span>
              <span className="block text-xs text-fg-muted">{t(`aiReports.type.${r.type}`, { defaultValue: r.type })}</span>
            </span>
          </span>
        );
      },
      sortValue: (r) => r.title,
      searchValue: (r) => `${r.title} ${t(`aiReports.type.${r.type}`, { defaultValue: r.type })}`,
    },
    {
      id: 'period',
      header: t('aiReports.columns.period', { defaultValue: 'Period' }),
      cell: (r) => <span className="font-mono text-xs">{r.periodFrom} → {r.periodTo}</span>,
      sortValue: (r) => r.periodFrom,
    },
    {
      id: 'language',
      header: t('aiReports.columns.language', { defaultValue: 'Language' }),
      cell: (r) => <Badge variant="outline">{r.language === 'ar' ? 'العربية' : 'English'}</Badge>,
      sortValue: (r) => r.language,
    },
    {
      id: 'status',
      header: t('aiReports.columns.status', { defaultValue: 'Status' }),
      cell: (r) => <StatusBadge status={r.status} />,
      sortValue: (r) => r.status,
    },
    {
      id: 'created',
      header: t('aiReports.columns.created', { defaultValue: 'Created' }),
      cell: (r) => (
        <span className="min-w-0">
          <span className="block text-sm">{fmtDate(r.createdAt)}</span>
          {r.createdByName && <span className="block truncate text-xs text-fg-muted">{r.createdByName}</span>}
        </span>
      ),
      sortValue: (r) => r.createdAt,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Sparkles className="h-6 w-6 text-brand" aria-hidden />
            {t('aiReports.title', { defaultValue: 'AI Reports' })}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">{t('aiReports.subtitle', { defaultValue: 'AI-written analytical reports over your real numbers' })}</p>
        </div>
        {canManage && (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus className="me-1.5 h-4 w-4" aria-hidden />
            {t('aiReports.new', { defaultValue: 'New report' })}
          </Button>
        )}
      </header>

      {isLoading || !data ? (
        <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={<Sparkles className="h-8 w-8" aria-hidden />}
          title={t('aiReports.empty.title', { defaultValue: 'No reports yet' })}
          description={t('aiReports.empty.hint', { defaultValue: 'Generate your first AI report — pick a report type and a period, and the analyst writes it from your live data.' })}
          {...(canManage ? {
            action: (
              <Button type="button" onClick={() => setCreateOpen(true)}>
                <Plus className="me-1.5 h-4 w-4" aria-hidden />
                {t('aiReports.new', { defaultValue: 'New report' })}
              </Button>
            ),
          } : {})}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            data={data.items}
            columns={columns}
            rowKey={(r) => r.id}
            searchable
            onRowClick={(r) => navigate(`/reports/ai/${r.id}`)}
            initialSort={{ id: 'created', dir: 'desc' }}
          />
        </Card>
      )}

      <AiReportCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
